import { Injectable, inject } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { CompanyFeatures } from "@shared/api-client-services/rentals/company/features/rentals-company-features-base.client.interface";
import { RentalsCompanyFeaturesBaseClient } from "@shared/api-client-services/rentals/company/features/rentals-company-features-base.client.service";
import { Company } from "@shared/api-client-services/rentals/company/rentals-company-base.client.interface";
import { RentalsCompanyBaseClient } from "@shared/api-client-services/rentals/company/rentals-company-base.client.service";
import { BehaviorSubject, distinctUntilChanged, map, skipWhile } from "rxjs";

@Injectable({ providedIn: 'root' })
export class CompanyFacilityService {
    public companyId$ = new BehaviorSubject<number>(0);
    public companyData$ = new BehaviorSubject<Company>(null);
    public companyFeatures$ = new BehaviorSubject<CompanyFeatures>(null);

    public manifestDisplayMessage$ = this.companyData$.pipe(map((company) => {
        const message = company?.manifestDisplayMessage || '';
        return this.domSanitizer.bypassSecurityTrustHtml(message);
    }));

    private rentalsCompanyBaseClient = inject(RentalsCompanyBaseClient);
    private rentalsCompanyFeaturesBaseClient = inject(RentalsCompanyFeaturesBaseClient);
    private domSanitizer = inject(DomSanitizer);

    constructor() {
        this.companyId$.pipe(
            skipWhile((id) => !id),
            distinctUntilChanged()
        ).subscribe(() => this.refresh());
    }

    public refresh() {
        if (!this.companyId$.getValue()) {
            throw new Error('No company id set');
        }

        this.refreshCompanyData();
        this.refreshCompanyFeatures();
    }

    public setCompanyId(companyId: number) {
        this.companyId$.next(companyId);
    }

    public refreshCompanyData() {
        const id = this.companyId$.getValue();

        if (!id) {
            return;
        }

        this.rentalsCompanyBaseClient.getCompanyById(id).subscribe((company) => {
            this.companyData$.next(company);
        });
    }

    public refreshCompanyFeatures() {
        const id = this.companyId$.getValue();

        if (!id) {
            return;
        }

        this.rentalsCompanyFeaturesBaseClient.getCompanyFeatures().subscribe((companyFeatures) => {
            this.companyFeatures$.next(companyFeatures);
        });
    }

    public getFeatureActiveStatus(featureAreaName: string) {
        let active = false;

        const features = this.companyFeatures$.getValue();
        const featureArea = features.featureAreas.find(feat => feat.name.toLowerCase().trim() === featureAreaName.toLowerCase().trim());
        
        if (featureArea?.isActive) {
            active = true;
        }

        return active;
    }

    public getFeatureChildActiveStatus(featureAreaName: string, childFeatureName: string) {
        let active = false;

        const features = this.companyFeatures$.getValue();
        const featureArea = features.featureAreas.find(feat => feat.name.toLowerCase().trim() === featureAreaName.toLowerCase().trim());

        if (featureArea?.isActive) {
            const feature = featureArea.features.find(feat => feat.name.toLowerCase().trim() === childFeatureName.toLowerCase().trim());

            if (feature?.isActive) {
                active = true;
            }
        }

        return active;
    }
}